import { useQueries } from '@tanstack/react-query';
import { useDashboard } from '../../api/dashboard.js';
import { PageWrapper } from '../../shell/PageWrapper.js';
import { apiFetch } from '../../api/client.js';
import type { Membership, Tenant, TenantKpis } from '../../api/types.js';
import { isValidatorOnly } from '../../lib/role.js';
import { AwaitingValidationWidget } from './AwaitingValidationWidget.js';
import { SocieteCard } from './SocieteCard.js';
import { RecentInvoicesTable } from './RecentInvoicesTable.js';
import { MentionsStream } from './MentionsStream.js';

interface Props {
  tenants: Tenant[];
  memberships: Membership[];
}

// SCI tenants are detected by legalName prefix until `tenant.kind` lands
// on the contract. Mirror of the helper in AppShell.
function isSciTenant(t: Tenant): boolean {
  return /^SCI\s/i.test(t.legalName);
}

// REQ-FE-029c — Org-wide dashboard. The page leads with "Vue d'ensemble_"
// + a card grid per société (ops + dormant SCIs same visual weight per
// `feedback_sci_not_muted`), then a cross-tenant recent-invoices table
// + a mentions stream (both placeholders pending BE endpoints), then
// the personal-context widgets (AssignedToMeWidget +
// AwaitingValidationWidget) preserved from REQ-FE-012/REQ-FE-014.
export function DashboardScreen({ tenants, memberships }: Props) {
  const tenantIds = memberships.map((m) => m.tenantId);
  const dash = useDashboard(tenantIds);

  const validatorOnly = isValidatorOnly(memberships);

  const accessible = tenants.filter((t) => tenantIds.includes(t.tenantId));
  const opsTenants = accessible.filter((t) => !isSciTenant(t));
  const sciTenants = accessible.filter(isSciTenant);

  // REQ-FE-029c-2 — Cross-tenant subtitle aggregation. Fan out
  // `useDashboardKpis` over every accessible tenant (shared react-query
  // cache with SocieteCard, no extra fetches in practice) and sum the
  // openAp + overdue + disputed counts so the subtitle reads
  //   "{X} urgentes · {Y} en retard sur {Z} à traiter à travers les {N}
  //    sociétés du groupe."
  const kpiQueries = useQueries({
    queries: tenantIds.map((tid) => ({
      queryKey: ['dashboard-kpis', tid] as const,
      queryFn: () => apiFetch<TenantKpis>(`/dashboard/kpis?tenantId=${encodeURIComponent(tid)}`),
      staleTime: 60_000,
    })),
  });
  const kpisReady = kpiQueries.every((q) => !q.isLoading);
  let totalATraiter = 0;
  let totalUrgentes = 0;
  for (const r of kpiQueries) {
    if (r.data) {
      totalATraiter += r.data.openAp?.count ?? 0;
      totalUrgentes += r.data.overdue?.count ?? 0;
    }
  }

  return (
    <PageWrapper>
      <section className="dashboard" aria-label="Vue d'ensemble">
        <header className="dashboard__head">
          <h1 className="display dashboard__title" data-testid="dashboard-title">
            Vue d&apos;ensemble
            <span className="dashboard__title-accent" aria-hidden="true">
              _
            </span>
          </h1>
          <p className="muted dashboard__subtitle" data-testid="dashboard-subtitle">
            {/* REQ-FE-032 area C15: drop the "Bonjour {name}." prefix —
              mockup goes straight to counts. */}
            {kpisReady && accessible.length > 0 ? (
              <>
                <strong className="text-coral">{totalUrgentes} urgentes</strong>
                {' · '}
                <strong className="text-coral">{totalUrgentes} en retard</strong>
                {` sur ${totalATraiter} à traiter à travers `}
                les {accessible.length} {accessible.length === 1 ? 'société' : 'sociétés'} du
                groupe.
              </>
            ) : (
              <>
                Vue agrégée à travers vos {accessible.length}{' '}
                {accessible.length === 1 ? 'société' : 'sociétés'}.
              </>
            )}
          </p>
          {/* REQ-FE-032 area C16: orphaned "Comptable AP" role pill removed
            — role info now lives in the sidebar admin footer. */}
        </header>

        {!validatorOnly && opsTenants.length > 0 ? (
          <section
            className="dashboard__societes"
            data-testid="dashboard-societes-ops"
            aria-label="Sociétés opérationnelles"
          >
            <div className="eyebrow dashboard__group-label">Sociétés opérationnelles</div>
            <div className="dashboard__societes-grid">
              {opsTenants.map((t) => (
                <SocieteCard key={t.tenantId} tenant={t} />
              ))}
            </div>
          </section>
        ) : null}

        {!validatorOnly && sciTenants.length > 0 ? (
          <section
            className="dashboard__societes"
            data-testid="dashboard-societes-sci"
            aria-label="Sociétés civiles (immobilier)"
          >
            <div className="eyebrow dashboard__group-label">Immobilier (SCI)</div>
            <div className="dashboard__societes-grid dashboard__societes-grid--sci">
              {sciTenants.map((t) => (
                <SocieteCard key={t.tenantId} tenant={t} dormantFallback />
              ))}
            </div>
          </section>
        ) : null}

        {!validatorOnly ? <RecentInvoicesTable /> : null}
        {!validatorOnly ? <MentionsStream /> : null}

        {/* REQ-FE-032 area C17: "Vue personnelle" block (AssignedToMeWidget +
          AwaitingValidationWidget) removed from the dashboard — mockup
          `01-dashboard.png` has no such block. Components stay alive in
          `screens/dashboard/` for a future `/me` route.
          Validator-only callers still need the awaiting-validation
          widget (the dashboard is otherwise empty for them), so it
          stays mounted in that single case. */}
        {validatorOnly ? (
          <section className="dashboard__personal" aria-label="Validations à faire">
            {dash.isLoading ? (
              <div className="dashboard-skeleton" data-testid="dashboard-loading" role="status">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="invoice-row invoice-row--skeleton">
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                ))}
              </div>
            ) : dash.isError ? (
              <div className="invoices__error" role="alert" data-testid="dashboard-error">
                <span>Impossible de charger le tableau de bord.</span>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => dash.refetch()}
                  data-testid="dashboard-retry"
                >
                  Réessayer
                </button>
              </div>
            ) : (
              <div className="dashboard-grid" data-testid="dashboard-grid">
                <AwaitingValidationWidget
                  validations={dash.data?.awaitingMyValidation ?? []}
                  tenants={tenants}
                />
              </div>
            )}
          </section>
        ) : null}
      </section>
    </PageWrapper>
  );
}
